import { type Alternative, type User } from '../../../types/alternatives-voting'
import { VoteButtons } from './VoteButtons'
import { Crown } from 'lucide-react'
import { ClickableUser } from '../../ClickableUser'

interface AlternativeItemProps {
  alternative: Alternative
  proposer?: User
  userVote?: 'upvote' | 'downvote'
  isWinner?: boolean
  rank: number
  onVote?: (alternativeId: string, voteType: 'upvote' | 'downvote') => void
}

export function AlternativeItem({
  alternative,
  proposer,
  userVote,
  isWinner,
  rank,
  onVote,
}: AlternativeItemProps) {
  return (
    <div
      className={`p-6 rounded-xl border-2 transition-all ${
        isWinner
          ? 'border-amber-500/30 bg-amber-500/5'
          : 'border-slate-800 bg-slate-900 hover:border-slate-700'
      }`}
    >
      {isWinner && (
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center gap-2 px-3 py-1 bg-amber-500 text-slate-900 text-xs font-bold rounded-full">
            <Crown className="w-3 h-3" />
            <span>الأكثر تصويتاً</span>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          {/* Rank Badge */}
          <div className="flex items-center gap-3 mb-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                rank === 1
                  ? 'bg-amber-500 text-slate-900'
                  : rank === 2
                  ? 'bg-rose-500/20 text-rose-400'
                  : 'bg-slate-800 text-slate-500'
              }`}
            >
              {rank}
            </div>
            <h3 className="text-2xl font-bold text-white">
              {alternative.egyptianWord}
            </h3>
          </div>

          <p className="text-slate-300 mb-4 leading-relaxed">
            {alternative.explanation}
          </p>

          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span>مقترح من</span>
            {proposer ? (
              <ClickableUser userId={proposer.id} userName={proposer.name} className="text-amber-500 font-medium" />
            ) : (
              <span className="text-amber-500 font-medium">مجهول</span>
            )}
          </div>

          {/* Net Score */}
          <div className="mt-3 inline-block px-3 py-1 bg-slate-800 rounded-full text-sm">
            <span className="text-slate-400">النتيجة: </span>
            <span className={`font-bold ${
              alternative.netScore > 0 ? 'text-amber-500' :
              alternative.netScore < 0 ? 'text-rose-500' :
              'text-slate-400'
            }`}>
              {alternative.netScore > 0 && '+'}{alternative.netScore}
            </span>
          </div>
        </div>

        <VoteButtons
          alternativeId={alternative.id}
          upvotes={alternative.upvotes}
          downvotes={alternative.downvotes}
          userVote={userVote}
          onVote={onVote}
        />
      </div>
    </div>
  )
}
